<script lang="ts">
	import Carousel from "@components/common/Carousel.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Icon from "@components/common/Icon.svelte";
	import type { EmblaCarouselType } from "embla-carousel";
	import type { ClassValue } from "svelte/elements";
	import arrowIcon from "@core/assets/icons/arrow.svg?raw";

	interface BenefitGridCard {
		title: string;
		description: string;
		class?: ClassValue;
		headingClass?: ClassValue;
		textClass?: ClassValue;
	}

	const {
		section,
		sectionClass,
		captionClass,
		title,
		cards,
		cardClass,
		cardSizeClass = "w-80 h-40",
		desktopWrapperClass,
	}: {
		section: string;
		sectionClass?: ClassValue;
		captionClass?: ClassValue;
		title: string;
		cards: BenefitGridCard[];
		cardClass?: ClassValue;
		cardSizeClass?: ClassValue;
		desktopWrapperClass?: ClassValue;
	} = $props();

	let canNext = $state(false);
	let canPrev = $state(false);
	let api = $state<EmblaCarouselType>();

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet benefitCard(card: BenefitGridCard)}
	<article
		class={[
			"p-5 flex flex-col items-center justify-center gap-2 text-center shrink-0",
			cardSizeClass,
			cardClass,
			card.class,
		]}
	>
		<Heading
			level={3}
			class={["text-4/tighter font-antipol font-bold uppercase", card.headingClass]}
			text={card.title}
		/>
		<p class={["text-xs/tighter font-light", card.textClass]}>
			<Formatted text={card.description} />
		</p>
	</article>
{/snippet}

<section class={["py-4 md:py-8 lg:py-12 xl:py-16 full-width grid-container", sectionClass]}>
	<div class={["mb-4 text-center uppercase container-6 font-bold tracking-[.1em]", captionClass]}>
		<Formatted text={section} />
	</div>

	<Heading level={2} text={title} class="lg:mb-15 heading-2 text-fcrdarkgray sm:max-w-125 text-center sm:mx-auto" />
	<div class={["gap-2 text-fcrdarkgray shrink-0 ml-auto my-9", canNext || canPrev ? "flex lg:hidden" : "hidden"]}>
		<button
			type="button"
			class={[
				"size-16 flex items-center bg-white justify-center border-fcrlightgray-300 disabled:opacity-50 border rounded-full",
				"[&:not(:disabled):hover:active]:scale-95 transition duration-150 ease-in-out",
				"[&:not(:disabled):hover]:border-fcrdarkgray",
			]}
			onclick={(): void => {
				api?.scrollPrev();
			}}
			disabled={!canPrev}
		>
			<Icon icon={arrowIcon} class="size-4.5 rotate-180" />
		</button>
		<button
			type="button"
			class={[
				"size-16 flex items-center bg-white justify-center border-fcrlightgray-300 disabled:opacity-50 border rounded-full",
				"[&:not(:disabled):hover:active]:scale-95 transition duration-150 ease-in-out",
				"[&:not(:disabled):hover]:border-fcrdarkgray",
			]}
			onclick={(): void => {
				api?.scrollNext();
			}}
			disabled={!canNext}
		>
			<Icon icon={arrowIcon} class="size-4.5" />
		</button>
	</div>
	<div class="lg:hidden grid-container full-width">
		<Carousel
			bind:canNext
			bind:canPrev
			bind:api
			items={cards}
			class="full-width"
			alignWithGridContainer
			itemsContainerClass="gap-4"
			options={{ dragFree: true }}
		>
			{#snippet item(card: BenefitGridCard)}
				{@render benefitCard(card)}
			{/snippet}
		</Carousel>
	</div>
	<div class={["hidden lg:flex px-4 justify-center lg:py-10 xl:py-14 2xl:py-17.5", desktopWrapperClass]}>
		<div class="flex flex-wrap justify-center gap-4.5">
			{#each cards as card (card.title)}
				{@render benefitCard(card)}
			{/each}
		</div>
	</div>
</section>
